import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AppShell } from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import TodayPage from "@/pages/TodayPage";
import DiscoverPage from "@/pages/DiscoverPage";
import AccountsPage from "@/pages/AccountsPage";
import PipelinePage from "@/pages/PipelinePage";
import PlannerPage from "@/pages/PlannerPage";
import PlaybooksPage from "@/pages/PlaybooksPage";
import SettingsPage from "@/pages/SettingsPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import TrashPage from "@/pages/TrashPage";
import CapturePage from "@/pages/CapturePage";
import ManagerQueuePage from "@/pages/ManagerQueuePage";
import MfaSetupPage from "@/pages/MfaSetupPage";

function Protected({ children }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Loading…</div>;
  if (!me) return <Navigate to="/login" state={{ from: location }} replace />;
  if ((me.mfa_enrolment_required || me.password_change_required) && location.pathname !== "/security/setup") return <Navigate to="/security/setup" replace />;
  return children;
}

function SetupOnly({ children }) {
  const { me, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Loading…</div>;
  if (!me) return <Navigate to="/login" replace />;
  return children;
}

function OrgRequired({ children }) {
  const { me, isPlatformAdmin } = useAuth();
  if (!me?.org) return isPlatformAdmin ? <Navigate to="/platform" replace /> : <div className="page"><div className="panel p-6 text-sm">Your account is not a member of an active organisation. Ask your administrator for an invite.</div></div>;
  return children;
}

function PlatformOnly({ children }) {
  const { isPlatformAdmin } = useAuth();
  return isPlatformAdmin ? children : <Navigate to="/today" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/security/setup" element={<SetupOnly><MfaSetupPage /></SetupOnly>} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/today" element={<OrgRequired><TodayPage /></OrgRequired>} />
              <Route path="/discover" element={<OrgRequired><DiscoverPage /></OrgRequired>} />
              <Route path="/accounts" element={<OrgRequired><AccountsPage /></OrgRequired>} />
              <Route path="/pipeline" element={<OrgRequired><PipelinePage /></OrgRequired>} />
              <Route path="/planner" element={<OrgRequired><PlannerPage /></OrgRequired>} />
              <Route path="/playbooks" element={<OrgRequired><PlaybooksPage /></OrgRequired>} />
              <Route path="/trash" element={<OrgRequired><TrashPage /></OrgRequired>} />
              <Route path="/capture" element={<OrgRequired><CapturePage /></OrgRequired>} />
              <Route path="/queue" element={<OrgRequired><ManagerQueuePage /></OrgRequired>} />
              <Route path="/settings/*" element={<SettingsPage />} />
              <Route path="/platform" element={<PlatformOnly><PlatformAdminPage /></PlatformOnly>} />
              <Route path="/" element={<Navigate to="/today" replace />} />
              <Route path="*" element={<Navigate to="/today" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" closeButton visibleToasts={3} />
      </AuthProvider>
    </div>
  );
}

export default App;
